/**
 * @uwu/contracts — zod schemas ที่ API (worker) และ SPA (web) ใช้ร่วมกัน
 * แหล่งความจริงของรูปร่างข้อมูล; OpenAPI จะ generate จากไฟล์นี้ (M0: subset)
 */
import { z } from "zod";
import { TicketSort } from "./views.ts";
import { HandoffTo } from "./handoff.ts";
import { TransitionPendingApproval } from "./approvals.ts";

export const TICKET_ID_RE = /^[A-Z0-9]{2,4}-\d{11}$/;

export const ErrorResponse = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    request_id: z.string().optional(),
    /** [SDKR-R1 · #369] machine identifier ของสาเหตุ (stable, lowercase snake_case) — additive เท่านั้น:
     *  error ที่ไม่มี reason ห้ามส่ง field นี้เลย · unknown/future reason ต้องส่งผ่านเป็น string ตรง ๆ (forward compat)
     *  ห้าม parse message มนุษย์/prefix details เป็น contract */
    reason_code: z.string().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const I18nText = z.record(z.string(), z.string()); // { th: "...", en: "..." }

export const UserKind = z.enum(["staff", "customer", "guest", "service"]);
export const TeamRole = z.enum(["team_lead", "member", "viewer"]);

export const UserSummary = z.object({
  id: z.uuid(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  kind: UserKind,
});
export type UserSummary = z.infer<typeof UserSummary>;

export const TeamSummary = z.object({
  id: z.uuid(),
  code: z.string(),
  name: I18nText,
  department_id: z.uuid().nullable(),
});
export type TeamSummary = z.infer<typeof TeamSummary>;

export const Me = z.object({
  user: UserSummary.extend({
    primary_email: z.string().nullable(),
    locale: z.string(),
    timezone: z.string(),
    is_admin: z.boolean(),
    needs_profile: z.boolean(), // display_name ยังไม่ตั้ง
  }),
  teams: z.array(TeamSummary.extend({ role: TeamRole })),
  is_staff: z.boolean(),
});
export type Me = z.infer<typeof Me>;

export const UpdateProfile = z.object({
  display_name: z.string().trim().min(1).max(80).optional(),
  locale: z.enum(["th", "en"]).optional(),
  timezone: z.string().max(64).optional(),
});
export type UpdateProfile = z.infer<typeof UpdateProfile>;

export const StatusType = z.enum(["open", "pending_customer", "pending_internal", "resolved", "closed"]);
export const Channel = z.enum(["email", "form", "api", "manual", "internal", "mailbox_convert"]);

export const StatusRef = z.object({ id: z.uuid(), code: z.string(), name: I18nText, type: StatusType, color: z.string() });
export const PriorityRef = z.object({ id: z.uuid(), code: z.string(), name: I18nText, rank: z.number().int(), color: z.string() });
export const ServiceRef = z.object({ id: z.uuid(), code: z.string(), name: I18nText });
// [W8 NP-1b · issue #1 item 2] export type ที่หายไป — เรียกใช้ฝั่ง web/API โดยไม่ต้อง re-derive เอง
export type StatusRef = z.infer<typeof StatusRef>;
export type PriorityRef = z.infer<typeof PriorityRef>;
export type ServiceRef = z.infer<typeof ServiceRef>;

export const Holder = z.object({
  team: TeamSummary,
  user: UserSummary.nullable(),
});
export type Holder = z.infer<typeof Holder>;

export const TicketSummary = z.object({
  id: z.string().regex(TICKET_ID_RE), // public id
  uuid: z.uuid(),
  version: z.number().int(),
  service: ServiceRef,
  channel: Channel,
  subject: z.string(),
  preview: z.string(),
  status: StatusRef,
  priority: PriorityRef,
  tags: z.array(z.string()),
  requester: UserSummary.extend({ email: z.string().nullable() }),
  holder: Holder,
  due_at: z.iso.datetime().nullable(),
  /** [W3 #49] snooze (T-036) — null = ไม่ได้ snooze */
  snoozed_until: z.iso.datetime().nullable().optional(),
  language: z.string().nullable(),
  counts: z.object({ messages: z.number().int(), internal_notes: z.number().int(), attachments: z.number().int() }),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  last_activity_at: z.iso.datetime(),
  last_customer_message_at: z.iso.datetime().nullable(),
  /** [Lead #75] ปิดเมื่อ — มีทุก view (null = ยังไม่ปิด) */
  closed_at: z.iso.datetime().nullable().optional(),
  /** [Lead #75] portal: ทีมตอบหลังข้อความล่าสุดของผู้แจ้ง (badge "ตอบใหม่") — มีเฉพาะ view ของลูกค้า */
  unread_for_requester: z.boolean().optional(),
  /** [Lead #75] portal: คะแนน CSAT ของการ์ดนี้ (null = ยังไม่ให้คะแนน) — มีเฉพาะ view ของลูกค้า */
  csat: z.object({ score: z.number().int(), comment: z.string().nullable(), at: z.iso.datetime() }).nullable().optional(),
  /** [T-063 R3] ตอนนี้รอใครตอบ: pending_customer → "customer" · open/pending_internal → "team" · resolved/closed → null (มีทุก viewer) */
  waiting_on: z.enum(["customer", "team"]).nullable().optional(),
  /** [#93] staff inbox row — ผู้เรียกยังไม่อ่าน: มีข้อความ public (ไม่ถูกลบ) seq > last_read_seq ของคนนี้ · มีเฉพาะ list ของ staff */
  staff_unread: z.boolean().optional(),
  /** [#93] staff inbox row — จำนวน watcher ที่ยังมีชีวิต (user+team) · มีเฉพาะ list ของ staff */
  watchers_count: z.number().int().optional(),
  /** [#93] staff inbox row — delivery_status ล่าสุดของข้อความตอบกลับ public ออก (bounced/failed → ชิป "ส่งไม่สำเร็จ") · null = ไม่มี · มีเฉพาะ list ของ staff */
  last_delivery_status: z.string().nullable().optional(),
  /** [#93] staff inbox row — เหตุผล triage จาก event `routed` ล่าสุด (rule=ai มี confidence/team_code) · null = ไม่มี event · มีเฉพาะ list ของ staff */
  triage_reason: z
    .object({
      rule: z.string().optional(),
      confidence: z.number().optional(),
      team_code: z.string().optional(),
    })
    .nullable()
    .optional(),
  /** [T-074 · #213] service mode list — มีไฟล์แนบอย่างน้อยหนึ่งชิ้น (detail ค่อยมี attachments[] ใน messages) */
  has_attachments: z.boolean().optional(),
});
export type TicketSummary = z.infer<typeof TicketSummary>;

export const MessageKind = z.enum(["public", "internal", "system", "forward", "ai_suggestion"]);
export const Message = z.object({
  id: z.uuid(),
  seq: z.number().int(),
  kind: MessageKind,
  direction: z.enum(["inbound", "outbound", "none"]),
  author: UserSummary.nullable(),
  author_display: z.string().nullable(),
  from_email: z.string().nullable(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  body_html: z.string(),
  body_text: z.string(),
  has_quoted: z.boolean(),
  delivery_status: z.string().nullable(),
  // [T-022] item ตรง `Attachment` (attachments.ts) — เพิ่มฟิลด์ optional เท่านั้น
  attachments: z.array(
    z.object({
      id: z.uuid(),
      filename: z.string(),
      mime: z.string(),
      size: z.number().int(),
      risk_flag: z.boolean(),
      inline_cid: z.string().nullable().optional(),
      ticket_id: z.string().nullable().optional(),
      url: z.string().nullable().optional(),
      created_at: z.iso.datetime().optional(),
    }),
  ),
  created_at: z.iso.datetime(),
  // [T-020/T-022] undo/send window — nullable optional เพื่อ backward-compatible กับ caller เดิม
  undo_until: z.iso.datetime().nullable().optional(),
  send_after: z.iso.datetime().nullable().optional(),
  sent_at: z.iso.datetime().nullable().optional(),
  // [T-029a #26] timeline ของ delivery status (bridge เขียนจาก outbox message_events) — staff เท่านั้น customer view omit
  delivery_events: z.array(z.object({ type: z.string(), at: z.iso.datetime(), data: z.record(z.string(), z.unknown()).optional() })).optional(),
  // [T-036] มี redaction overlay ถูก apply กับข้อความนี้ (04 §7)
  redacted: z.boolean().optional(),
  /** [T-063 R5] ข้อความนี้มีส่วนที่ถูกปกปิดอยู่ (ต่อผู้เรียก — ผู้เห็นต้นฉบับ = false) — ไว้ติดป้ายโดยไม่ต้องยิง GET …/redactions */
  has_redaction: z.boolean().optional(),
  /** [T-063 R6] public reply ของทีมงาน: true = สร้าง/เข้าคิวอีเมลแล้ว · false = ปิดอีเมลไว้ (ลูกค้าอ่านในพอร์ทัล) */
  email_sent: z.boolean().optional(),
  /** [T-067] apply.status_code ติด gate การอนุมัติ → ข้อความถูกบันทึกแล้ว แต่สถานะยังไม่เปลี่ยน (รอครบผู้อนุมัติ) */
  apply_result: z.lazy(() => TransitionPendingApproval).optional(),
  /** [T-083 · #237] service reply ที่ส่ง Idempotency-Key: true = คืนข้อความเดิมจากแถว idempotency (ไม่ได้สร้างใหม่) — มีเฉพาะ route service reply ที่ส่ง header */
  idempotent_replay: z.boolean().optional(),
  /** [#92 · rv92 B1] ข้อความถูกปักหมุดไว้บนบทสนทนา (messages.pinned_at) — ปักได้/เห็นได้เฉพาะ staff view (serializer omit สำหรับ customer) · optional เพื่อ backward-compatible */
  pinned: z.boolean().optional(),
});
export type Message = z.infer<typeof Message>;

export const TicketEvent = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  type: z.string(),
  actor: z.object({ type: z.string(), id: z.string().nullable(), display: z.string().nullable() }),
  data: z.record(z.string(), z.unknown()),
  visible_to_customer: z.boolean(),
});

export const Ticket = TicketSummary.extend({
  messages: z.array(Message),
  events: z.array(TicketEvent),
  participants: z.array(z.object({ email: z.string(), name: z.string().nullable(), role: z.enum(["requester", "cc"]) })),
  custom_fields: z.record(z.string(), z.unknown()),
  allowed_transitions: z.array(
    z.object({
      to_status: StatusRef,
      label: I18nText,
      // [T-067] transition นี้ต้องอนุมัติก่อนเปลี่ยน — ให้หน้าเว็บติดป้ายล่วงหน้าได้ (optional เพราะ serializer เดิมยังไม่ใส่)
      requires_approval: z.boolean().optional(),
    }),
  ),
  can: z.object({
    reply: z.boolean(),
    note: z.boolean(),
    edit: z.boolean(),
    claim: z.boolean(),
    handoff: z.boolean(),
    /** [T-074 · #213] service mode (customer view) — ปิดเคสเองได้ (มี transition → closed ที่ requester ทำได้) */
    close: z.boolean().optional(),
    /** [T-074 · #213] service mode (customer view) — เปิดเรื่องใหม่ได้ (transition → open + หน้าต่าง reopen ยังไม่หมด) */
    reopen: z.boolean().optional(),
    /** [T-074 · #213] service mode (customer view) — ให้คะแนน CSAT ได้ (resolved/closed ≤ 14 วัน และยังไม่ให้คะแนน) */
    rate: z.boolean().optional(),
  }),
  /** [T-063 R8] ผู้เรียกตอบกลับ (public) ได้ไหมตอนนี้ — มุมมองลูกค้า = false เมื่อการ์ดปิดแล้ว (ทีมงาน = true เสมอ) */
  can_reply: z.boolean().optional(),
  /** [T-063 R6] ผู้แจ้งมีทางรับรู้อื่นนอกจากอีเมลไหม (มี user ที่ล็อกอินได้ ไม่ใช่ guest ที่มีแต่อีเมล) — ใช้เตือนก่อนปิดอีเมล */
  requester_has_portal_access: z.boolean().optional(),
  /** [E-05 #102] ค่าเริ่มต้นการส่งอีเมลของ service นี้ (effective = services.default_notify_by_email ?? true) — มีเฉพาะ view ของ staff/admin (detail) · customer/portal omit */
  email_notify_default: z.boolean().optional(),
  /** Staff-only identity target; synthetic addresses never appear as recipients. */
  requester_sso_sub: z.uuid().nullable().optional(),
  email_notify_available: z.boolean().optional(),
  /** [#99] หน้าต่างที่ลูกค้าเปิดใหม่ได้หลังปิด (วัน) = status set reopen_closed_after_days ของการ์ด — detail เท่านั้น (customer+staff) · ตัวเลขเดียว ไม่เผย policy อื่นของชุดสถานะ */
  reopen_days: z.number().int().optional(),
  /** [T-051] CSAT ล่าสุดของการ์ด — staff view เท่านั้น (serializer ใส่เฉพาะ staff · ไม่มี = null) */
  csat: z.object({ score: z.number().int(), comment: z.string().nullable(), at: z.iso.datetime() }).nullable().optional(),
});
export type Ticket = z.infer<typeof Ticket>;

export const CreateTicket = z.object({
  service_code: z.string().regex(/^[A-Z0-9]{2,4}$/),
  subject: z.string().trim().min(1).max(998),
  body_html: z.string().max(200_000).optional(),
  body_text: z.string().max(200_000).optional(),
  requester: z
    .object({
      user_id: z.uuid().optional(),
      sso_sub: z.uuid().optional(),
      email: z.email().optional(),
      name: z.string().max(120).optional(),
    })
    .optional(),
  cc: z.array(z.email()).max(48).optional(),
  team_code: z.string().optional(),
  holder_user_id: z.uuid().optional(),
  priority_code: z.string().optional(),
  tags: z.array(z.string().max(64)).max(20).optional(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
  channel: Channel.optional(),
  imported: z.boolean().optional(),
  created_at: z.iso.datetime().optional(),
});
export type CreateTicket = z.infer<typeof CreateTicket>;

export const CreateMessage = z.object({
  kind: z.enum(["public", "internal"]),
  body_html: z.string().max(500_000),
  to: z.array(z.email()).max(50).optional(),
  cc: z.array(z.email()).max(48).optional(),
  attachment_ids: z.array(z.uuid()).max(20).optional(),
  send_at: z.iso.datetime().optional(),
  send_if_no_reply: z.boolean().optional(), // [T-054] ลูกค้าตอบก่อน send_at → ยกเลิกการส่ง (ใช้กับ public reply ของ staff เท่านั้น)
  snooze_until: z.iso.datetime().optional(), // [T-054] หลังส่งข้อความ → snooze การ์ด (T-036) ใน tx ถัดไป
  /** [T-063 R6] เลือกว่าคำตอบนี้ส่งอีเมลหรือไม่ — ใช้ได้เฉพาะ public ของทีมงาน (โน้ตภายในไม่เคยส่ง = 422) · ไม่ส่งมา = ตาม services.default_notify_by_email */
  notify_by_email: z.boolean().optional(),
  /** [#92] สำเนาลับ — ใช้ได้เฉพาะ public reply ของทีมงาน · ส่งเป็น Bcc ของอีเมลขาออก (ผ่าน suppression) และไม่ถูกเผยผ่าน to/cc ของ serializer เด็ดขาด */
  bcc: z.array(z.email()).max(48).optional(),
  /** [#92] ส่งจากกล่องนี้แทนกล่องของการ์ด — ต้องเป็น mailbox ที่ active และอยู่ใน service เดียวกับการ์ด (staff public เท่านั้น) ไม่ใช่ = 422 */
  from_mailbox_id: z.uuid().optional(),
  /** [#187] ลายเซ็นต่อข้อความ — ใช้ได้เฉพาะ public reply ของทีมงานที่ยังส่งอีเมล (นอกเหนือจากนี้ 422 path signature) ·
   *  string (≤200_000) = ใช้ลายเซ็น HTML นี้แทน (server sanitize ก่อนใช้ · ว่าง/ขาวล้วน = ไม่แนบ · ซ้ำกับเนื้อความ = ไม่แนบซ้ำ)
   *  false = ไม่แนบลายเซ็นเลย รวมที่บันทึกไว้ใน mail_accounts.signature_html
   *  ไม่ส่ง = ตามลายเซ็นที่บันทึกไว้ของ account ตามกล่องที่ใช้ส่ง (#181) ไม่มี = ไม่แนบ
   *  effective signature เป็น email-body เท่านั้น — ไม่ลงแถวข้อความ/ไทม์ไลน์/พอร์ทัล และไม่แตะ header ใดของอีเมล */
  signature: z.union([z.string().max(200_000), z.literal(false)]).optional(),
  apply: z.object({ status_code: z.string().optional(), holder_user_id: z.uuid().nullable().optional() }).optional(),
  expected_version: z.number().int().optional(),
});
export type CreateMessage = z.infer<typeof CreateMessage>;

/** [#92] ปักหมุด/เลิกปักหมุดข้อความ — staff เท่านั้น · สถานะเดิมไม่ตรง = 409 conflict (conditional update) */
export const MessagePinRequest = z.object({ pinned: z.boolean() });
export type MessagePinRequest = z.infer<typeof MessagePinRequest>;
export const MessagePinResponse = z.object({
  message_id: z.uuid(),
  pinned: z.boolean(),
  pinned_at: z.iso.datetime().nullable(),
});
export type MessagePinResponse = z.infer<typeof MessagePinResponse>;

export const TicketListQuery = z.object({
  view: z.enum(["mine", "team_unassigned", "team_all", "watching", "all", "mentioned", "snoozed"]).optional(),
  view_id: z.uuid().optional(),
  sort: TicketSort.optional(),
  team_id: z.uuid().optional(),
  status_type: z
    .union([StatusType, z.array(StatusType)])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]))
    .optional(),
  q: z.string().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type TicketListQuery = z.infer<typeof TicketListQuery>;

export const Paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), next_cursor: z.string().nullable() });

export const TicketList = Paginated(TicketSummary);
export type TicketList = z.infer<typeof TicketList>;

export const Transition = z.object({ to_status_code: z.string(), note: z.string().max(5000).optional(), expected_version: z.number().int().optional() });
export const Claim = z.object({ expected_version: z.number().int().optional() });
export type Transition = z.infer<typeof Transition>;
export type Claim = z.infer<typeof Claim>;

/** T-011 R7 — lookup บริการ/ทีม (staff) */
export const ServiceWithIntake = ServiceRef.extend({
  intake_team: TeamSummary.nullable(),
  /** [UX-C2 · UX-29] ตัวเลือกความสำคัญตาม scheme ของบริการ (ไม่ผูก scheme → default scheme ฝั่ง worker) — เรียงตาม rank แล้ว */
  priorities: z.array(PriorityRef),
});
export type ServiceWithIntake = z.infer<typeof ServiceWithIntake>;
export const TeamWithRole = TeamSummary.extend({
  my_role: TeamRole.nullable(),
  /** [UX-C2 · UX-50] แผนกจริงของทีม (ชื่อ + intake readiness) — ทีมไม่มีแผนก = null */
  department: z
    .object({
      id: z.uuid(),
      code: z.string(),
      name: I18nText,
      /** intake readiness: null = แผนกนี้ยังไม่มีทีมรับเรื่อง (ส่งต่อทั้งแผนกไม่ได้ — resolve-target 422) */
      intake_team_id: z.uuid().nullable(),
    })
    .nullable(),
});
export type TeamWithRole = z.infer<typeof TeamWithRole>;
// [W8 NP-1b · issue #1 item 1] GET /api/v1/services — list บริการ active ตาม convention `{items}` ของ list อื่น ๆ
export const ServicesList = z.object({ items: z.array(ServiceWithIntake) });
export type ServicesList = z.infer<typeof ServicesList>;

export const StaffInviteCreate = z.object({ email: z.email(), team_id: z.uuid(), role: TeamRole });
export * from "./auth.ts";
export * from "./inbound.ts";
export * from "./email.ts";
export * from "./email-relay.ts"; // [email-relay v1] intake protocol จาก Relay Worker ต่าง account — แยกจาก SDK ขาส่ง
export * from "./handoff.ts";
export * from "./views.ts";
export * from "./portal.ts";
export * from "./attachments.ts";
export * from "./notifications.ts"; // [T-031]
export * from "./webhooks.ts"; // T-033
export * from "./realtime.ts"; // [T-035]
export * from "./redaction.ts";
export * from "./relations.ts";
export * from "./workflow.ts"; // [T-030]
export * from "./admin.ts"; // [T-032]
export * from "./forms.ts"; // [T-034]
export * from "./kb.ts"; // [T-050]
export * from "./csat.ts"; // [T-051]
export * from "./ai.ts"; // [T-052]
export * from "./pdpa.ts"; // [T-053]
export * from "./ops.ts"; // [T-054]
export * from "./side.ts"; // [T-066]
export * from "./drafts.ts"; // [T-062]
export * from "./approvals.ts"; // [T-067]
export * from "./reports.ts"; // [T-065]
export * from "./mail.ts"; // [M4]
export * from "./mail-send.ts"; // [M4-05]
export * from "./mail-contacts.ts"; // [M4-11]
export * from "./mailboxes.ts"; // [#186]
export * from "./compose.ts"; // [UX-C2 · #295 W06]
export * from "./attachment-reservation.ts"; // [SDKR-R3 · #369]

// [T-057] staff inbox API — bulk actions + counts (ต่อท้ายไฟล์ ห้ามแก้ field เดิม)

export const BulkTicketAction = z.discriminatedUnion("type", [
  z.object({ type: z.literal("claim") }),
  z.object({ type: z.literal("release") }),
  z.object({ type: z.literal("transition"), to_status_code: z.string().min(1).max(64) }),
  z.object({ type: z.literal("priority"), code: z.string().min(1).max(32) }),
  z.object({
    type: z.literal("tags"),
    add: z.array(z.string().min(1).max(64)).max(20).optional(),
    remove: z.array(z.string().min(1).max(64)).max(20).optional(),
  }),
  z.object({ type: z.literal("snooze"), until: z.iso.datetime().nullable(), scope: z.enum(["card", "me"]).optional() }),
  z.object({
    type: z.literal("transfer"),
    to: HandoffTo,
    note: z.string().max(5000).optional(),
  }),
  z.object({ type: z.literal("close") }),
]);
export type BulkTicketAction = z.infer<typeof BulkTicketAction>;

export const BulkTicketRequest = z
  .object({
    /** ticket public id (SVC-YYMMNNNNNCC) — 1–100 ใบ ไม่ซ้ำ */
    ids: z.array(z.string().min(1).max(32)).min(1).max(100),
    action: BulkTicketAction,
  })
  .check((ctx) => {
    if (ctx.value.ids.length !== new Set(ctx.value.ids).size) {
      ctx.issues.push({ code: "custom", input: ctx.value, path: ["ids"], message: "ไอดีการ์ดซ้ำกันในรายการ" });
    }
  });
export type BulkTicketRequest = z.infer<typeof BulkTicketRequest>;

export const BulkTicketResultItem = z.object({
  id: z.string(),
  ok: z.boolean(),
  version: z.number().int().optional(),
  error: z.object({ code: z.string(), message: z.string() }).optional(),
  /** [T-067] action นี้ติด gate การอนุมัติ — ยังไม่สำเร็จ (ไม่นับใน ok) รอผู้อนุมัติ */
  pending_approval: z.lazy(() => TransitionPendingApproval).optional(),
});
export type BulkTicketResultItem = z.infer<typeof BulkTicketResultItem>;

export const BulkTicketResult = z.object({
  requested: z.number().int(),
  ok: z.number().int(),
  failed: z.number().int(),
  items: z.array(BulkTicketResultItem),
  /** [T-067] จำนวนที่รออนุมัติ (แยกจาก ok/failed) — ใส่เมื่อ > 0 เพื่อไม่กระทบผู้อ่านเดิม */
  pending: z.number().int().optional(),
});
export type BulkTicketResult = z.infer<typeof BulkTicketResult>;

export const TicketCounts = z.object({
  mine: z.number().int(),
  mentioned: z.number().int(),
  watching: z.number().int(),
  snoozed: z.number().int(),
  team_unassigned: z.number().int(),
  team_all: z.number().int(),
  /** null = feature flag `triage.inbox` ปิดอยู่ */
  triage: z.number().int().nullable(),
  saved_views: z.array(z.object({ id: z.string(), count: z.number().int() })),
});
export type TicketCounts = z.infer<typeof TicketCounts>;

// [UX-D1 · UX-43 · #295] portal /my — counts ทั้งหมดของการ์ดที่ผู้เรียกเป็นเจ้าของ (view=mine semantics)
// all = ทั้งหมดใน view · active = open+pending_customer+pending_internal · closed = resolved+closed (additive ใหม่)
export const MyTicketCounts = z.object({
  all: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
});
export type MyTicketCounts = z.infer<typeof MyTicketCounts>;
export * from "./email-control.ts";

// [SDKR-R2 · #369] ticket create replay contract (freeze §5)
export * from "./ticket-replay.ts";

// [TM-01..TM-03] โมดูลงานภายใน (internal tasks) — Lead scaffold · tasks/TM-CONTRACTS.md
export * from "./tasks.ts";

export * from "./version.ts";
